import 'react-toastify/dist/ReactToastify.css';

import Home from '@app/routes/home-dashboard';
import ProtectedRoute from '@app/routes/protected-route';
import PublicRoutes from '@app/routes/public-route';
import {
  ForgotPassword as ForgetPassword,
  GetOtp,
  Login,
  ResetPassword,
  Signup,
} from '@features/auth';
import HireR8tr from '@features/buyer/hire/hire-r8tr/hire-r8tr';
import Notes from '@features/buyer/hire/special-notes';
import SubmitPayment from '@features/buyer/hire/submit-payment/submit-payment';
import TipPayment from '@features/buyer/hire/tip-payment/tip-payment';
import UpdateHireR8tr from '@features/buyer/hire/update-hire-r8tr/hire-r8tr';
import BuyerRatingR8tr from '@features/buyer/jobs/buyer-rating-r8tr/buyer-rating-r8tr';
import SingleJobDetailBuyer from '@features/buyer/jobs/current-job-detail-buyer/current-job-detail-buyer';
import PdfPage from '@features/buyer/jobs/pdf-page/pdf-page';
import RatingDetail from '@features/buyer/jobs/rating-detail/rating-detail';
import UploadedVideos from '@features/buyer/jobs/uploaded-videos/uploaded-videos';
import { TermsAndConditions } from '@features/legal';
import Braintree from '@features/payments';
import {
  default as CompleteProfile,
  default as ProfileInformation,
} from '@features/profile/pages';
import WorkProfile from '@features/profile/work-profile';
import SingleJobDetail from '@features/r8tr/jobs/single-job-detail/single-job-detail';
import SpecialNotes from '@features/r8tr/jobs/special-notes/special-notes';
import VideoUpload from '@features/r8tr/jobs/video-upload/video-upload';
import Notification from '@features/r8tr/notifications/notification';
import R8ting from '@features/r8tr/rating/r8ting/r8ting';
import R8trRating from '@features/r8tr/rating/r8tr-rating/r8tr-rating';
import Settings from '@features/settings';
import Support from '@features/support';
import { useAppSelector } from '@lib/store/hooks';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import type { Socket } from 'socket.io-client';

interface AppRoutesProps {
  socket: Socket;
}

const AppRoutes = ({ socket }: AppRoutesProps): JSX.Element => {
  const mode = useAppSelector((state) => state.colorMode.mode);

  return (
    <Router basename="/">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={mode}
      />
      <Grid container>
        <CssBaseline />
        <Routes>
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/" element={<ProtectedRoute socket={socket} />}>
            <Route path="/ebay-hire-a-r8tr" element={<UpdateHireR8tr />} />
            <Route path="/upload-videos" element={<VideoUpload />} />
            <Route path="/buyer-rating-r8tr" element={<BuyerRatingR8tr />} />
            <Route path="/r8ting" element={<R8ting />} />
            <Route path="/work-profile" element={<WorkProfile />} />
            <Route
              path="/service-area"
              element={<Navigate to="/work-profile?step=areas" replace />}
            />
            <Route path="/special-notes" element={<SpecialNotes />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/r8tr-rating" element={<R8trRating />} />
            <Route path="/rating-detail" element={<RatingDetail />} />
            <Route path="/submit-payment" element={<SubmitPayment />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/support" element={<Support />} />
            <Route path="/tip-payment" element={<TipPayment />} />
            <Route path="/pdf" element={<PdfPage />} />
            <Route path="/uploaded-videos" element={<UploadedVideos />} />
            <Route
              path="/area-expertise"
              element={<Navigate to="/work-profile?step=expertise" replace />}
            />
            <Route
              path="/area-of-expertise"
              element={<Navigate to="/work-profile?step=expertise" replace />}
            />
            <Route
              path="/level-expertise"
              element={<Navigate to="/work-profile?step=expertise" replace />}
            />
            <Route path="/payment-methods" element={<Braintree />} />
            <Route path="/current-jobs" element={<Navigate to="/" replace />} />
            <Route path="/" element={<Home />} />
            <Route path="/:id" element={<Home />} />
            <Route path="/detail" element={<SingleJobDetail />} />
            <Route path="/job-detail" element={<SingleJobDetailBuyer />} />
            <Route path="/personal-info" element={<ProfileInformation />} />
            <Route
              path="/change-password"
              element={<Navigate to="/" replace />}
            />
            <Route path="/notification" element={<Notification />} />
            <Route path="/hire-a-r8tr" element={<HireR8tr />} />
          </Route>
          <Route path="/" element={<PublicRoutes />}>
            <Route path="/signup" element={<Signup />} />

            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<ForgetPassword />} />
            <Route path="/get-otp" element={<GetOtp />} />
          </Route>
        </Routes>
      </Grid>
    </Router>
  );
};

export default AppRoutes;
